import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { EntityTagSection } from "../components/EntityTagSection";
import { CommsTimeline } from "../components/CommsTimeline";
import type { UserTag } from "../api/types";

// The user behind a sign-up feed row (spec-3 ac-9): orgs, memexes, activity.
interface UserDetail {
  id: string;
  name: string | null;
  email: string;
  createdAt: string;
  memberships: { orgId: string; orgName: string; role: string }[];
  memexes: { id: string; name: string }[];
  activity: { mcpSessions: number; mcpToolCalls: number; docsCreated: number; activeDays: number };
  tags: UserTag[];
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "notfound" | "error">("loading");

  useEffect(() => {
    if (!id) return;
    api<{ user: UserDetail }>(`/api/users/${id}`)
      .then((r) => {
        setUser(r.user);
        setStatus("ok");
      })
      .catch((e) => setStatus(e?.status === 404 ? "notfound" : "error"));
  }, [id]);

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/" className="text-sm text-muted hover:text-primary">
        &larr; Dashboard
      </Link>

      {status === "loading" && <p className="mt-6 text-muted">Loading…</p>}
      {status === "notfound" && <p className="mt-6 text-status-danger-text">User not found.</p>}
      {status === "error" && <p className="mt-6 text-status-danger-text">Failed to load.</p>}

      {status === "ok" && user && (
        <>
          <header className="mt-4 mb-6">
            <h1 className="text-2xl text-heading" data-testid="user-name">
              {user.name ?? user.email}
            </h1>
            <p className="text-sm text-muted">
              {user.email} · joined {new Date(user.createdAt).toLocaleDateString()}
            </p>
          </header>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Active days" value={user.activity.activeDays} />
            <Stat label="MCP sessions" value={user.activity.mcpSessions} />
            <Stat label="Tool calls" value={user.activity.mcpToolCalls} />
            <Stat label="Docs created" value={user.activity.docsCreated} />
          </div>

          <Section title="Tags">
            <div className="px-4 py-3">
              <EntityTagSection entityType="user" entityId={user.id} initialTags={user.tags} />
            </div>
          </Section>

          <Section title="Organizations">
            {user.memberships.map((m) => (
              <Row key={m.orgId} to={`/orgs/${m.orgId}`} left={m.orgName} right={m.role} />
            ))}
          </Section>

          <Section title="Memexes">
            {user.memexes.map((m) => (
              <Row key={m.id} to={`/memexes/${m.id}`} left={m.name} />
            ))}
          </Section>

          <Section title="Communications">
            <CommsTimeline userId={user.id} />
          </Section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-edge bg-surface px-5 py-4">
      <div className="text-2xl font-semibold text-heading">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">{title}</h2>
      <div className="overflow-hidden rounded-xl border border-edge">{children}</div>
    </section>
  );
}

function Row({ to, left, right }: { to: string; left: string; right?: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between border-b border-edge-subtle px-4 py-2.5 last:border-0 hover:bg-card-hover"
    >
      <span className="font-medium text-primary">{left}</span>
      {right && <span className="text-xs text-muted">{right}</span>}
    </Link>
  );
}
